import { Injectable } from '@angular/core';

export class TuningText {
  notes: string;
  preferredNote12?: number;
  mds: string[];
  photos?: {name?: string, width?: number}[];
}

@Injectable({
  providedIn: 'root'
})
export class TuningsTextService {

  // source: https://web.archive.org/web/20120713033111/http://warrenallencom.ipage.com/waguitartunings/tunings.htm

  public all: TuningText[] = [{
    notes: 'E A D G B E',
    mds: [
      `This is the traditional standard tuning familiar to most guitarists, though it's probably 
      possible to grow up within the Hawaiian slack-key guitar community without learning much about it.`,
      `Slightly mad **Richard Lloyd** (of Television fame) suggests that a completely fourths-based 
      tuning for the guitar -- E A D G C F -- was historically rejected [though revived 
      by **Stanley Jordan** see above] because of the "god awful" minor 9th interval between the low 
      open E string and the high open F string.`,
      `Even if this is dubious, it seems conceivable that the natural invitation extended by the low 
      E string to play 1st position E major and E minor chords would be flatly contradicted by 2nd and 
      1st strings tuned to C and F respectively. 1st position six-string Em would require this 
      improbable fingering: Em:022042 (e b e g e g), though a reasonable alternative would be 
      Em:02204x (e b e g e). The 1st position six-string E major triad would require E:022143 
      (e b e g# e g#), which would distinctly favor six-fingered players (try it). The rest of us 
      would probably be left struggling with the hardly more-playable E:02214x (e b e g# e), losing 
      the high sparkle of string 1 entirely. After some awkward exploration of these chord shapes,
      some players, surely, would not fail to notice that the 2nd (C) and 1st (F) strings were perched 
      just one notch (one half-step) above ideal scale tones for E-based chords: B (5) and E (1),
      respectively. Dropping these strings correspondingly, they would experience the ease and the 
      massive sound of our now-familiar tonic-bracketed Em:022000 and E:022100 grips -- and forever 
      secure the unique guitar-friendliness of the key of E [- WA].`,
      `Even **Joni Mitchell** used standard tuning, at least early on... here she is 
      playing John Phillips's *Me and My Uncle* on Oscar Brand's TV show, *Let's Sing Out*,
      way back in 1965.`
    ]
  }, {
    notes: 'A D G C E A',
    mds: [
      `Invented by **Frank Gambale** to facilitate piano-like 'cluster' chord voicings,
      for chord-melody playing. "That tuning is particularly suited for chord-melody playing, not single-note 
      improvisations. Live, I play a double-neck, so I can have both tunings available [Standard and Gambale]. 
      The tuning has been a revelation. I gave up on harmonically-dense cluster voicings for the guitar because 
      they were a physical impossibilily. But I was messing with a Nashville tuning patch on a Roland VG-88 
      a few years ago and got the idea to develop this tuning, wherein the whole guitar is tuned up a fourth,
      but the top two strings are <em>down</em> an octave. I use a .010 gauge set for the bottom four strings,
      starting with the A [6th] string, and for the top two strings I use the D and G strings from a set 
      of .009s. So, low to high, it is .036, .026, .017, .013, .024, .016. It takes a moment to get used to 
      it, but within a few minutes, everyone I show it to loves it. The beauty of the tuning is you can play 
      the same shapes that you're used to, whereas if you use an alternate tuning like DADGAD, all of your 
      shapes go away and you have to relearn everything. In my tuning, if you play, say, a D chord shape, it's 
      still a major triad [a G major triad, as it would be on a ukulele, sort of]." - article in Guitar Player 
      magazine, April 2010.`
    ]
  }, {
    notes: 'G G D D D# D#',
    mds: ['A **Sonic Youth** / **Thurston Moore** invention.']
  }, {
    notes: 'F# F# F# F# G B',
    mds: ['A **Sonic Youth** / **Thurston Moore** invention.']
  }, {
    notes: 'e a d g B E',
    photos: [{name: 'Billy_Gibbons.jpg', width: 250}],
    mds: [
      `(aka 'Poor Man's 12-string') Developed by Nashville session cats, apparently. 
      The 6th, 5th, 4th and 3rd strings are replaced with lighter strings so they may be tuned an 
      octave higher -- producing an extraordinarily bright textural variation for recording sessions,
      with  other guitars typically in the foreground. As Rob McMinn (Music Tech magazine, Dec 2003) puts it,
      "Your guitar will now sound ethereal, light, bright, and jangly. Recorded this way, your acoustic 
      guitar will slice through the mix. Stick on a capo and play higher up the neck and it can even sound 
      like a mandolin!".`,
      `The 6th string is wound, the 5th string is wound or plain, and the other strings are plain. The 
      following string gauges are recommended for acoustic guitar by **Rob McMinn**.  They're the octave 
      strings from an acoustic 12-string set. The same or similar gauges would work well for electric 
      guitar:`,'',
      '6th string:  E - one octave higher  .025w',
      '5th string:  A - one octave higher  .017',
      '4th string:  D - one octave higher  .012',
      '3rd string:  G - one octave higher  .009',
      '2nd string:  B - normal pitch       .014',
      '1st string:  E - normal pitch       .010',
      '',
      `**Billy Gibbons** "Cheater's 12" variation: "You use a first string in place of the big E and tune 
      it in unison to your little E. Then you use a second string for the fifth, and tune it to an A. Use 
      another second string for your usual fourth string, tuned to a high D. So you've got standard tuning,
      but strange octaves - kind of a fake 12-string."  - Guitar Player magazine, March 1998.`,
      `**David Gilmour** uses **Gibbons** variant, or something very close to it for *Hey You* (on *The Wall*). 
      He uses unwound strings across the fretboard, replacing the 6th string with a high E string -- tuned 
      to the normal pitch for the 1st string (two octaves above the normal pitch for the 6th string). - Guitar 
      Legends magazine, late 1997.`
    ]
  }, {
    notes: 'E A D G C F',
    photos: [{name: 'Stanley_Jordan.jpg', width: 200}],
    mds: [
      `Used by 'touch-technique' (two-handed tapping) jazz virtuoso **Stanley Jordan**. The 1st and 2nd 
      strings are each tuned up a half step, so that the interval between *every* pair of strings is a perfect 
      fourth -- unlike in standard tuning, where the fourths are interrupted by a major third between the 2nd 
      and 3rd strings. Jordan's tuning makes for consistent chord grips across the fretboard -- any chord shape 
      may be moved to any other position on the fretboard without altering its harmonic structure. - Wikipedia 
      entry for **Stanley Jordan**, 30 Apr 2006.`,
      `"...**Stanley Jordan** recorded an instrumental cover of *Stairway to Heaven* which is played on 
      two guitars simultaneously, with no overdubs." - Wikipedia`
    ]
  }, {
    notes: 'E A D G# B E',
    mds: [
      `Used by **Joe Louis Walker** for slide playing. Tune string 3 up a half step, to G#. - 
      article in Guitar Player magazine, April 2010. Advantage: establishes a movable trebly major 
      triad on the top three strings. Disadvantage: replaces standard tuning's major triad on 
      strings 2, 3, and 4 with a diminished triad. Standard tuning also offers a minor triad on the 
      top three strings.`
    ]
  }, {
    notes: 'E A D G B D',
    mds: [
      `Used for Zulu *Maskanda* music and guitar style. "One variation on this replaces the 
      D fourth string with a nylon first string and tunes it in unison to the D first string. This is 
      known as "double first" tuning, pronounced *dabul fersi*" - article in Acoustic Guitar magazine,
      July 2004.`
    ]
  }, {
    notes: 'E A D F# B E',
    preferredNote12: 6,
    mds: [
      `Used by **John Renbourn** - *Bicycle Tune*, **John Dowland**, **Alonso de Mudarra**, other 
      classical guitarists. The lutes, tenor viola da gambas and vihuelas of the Renaissance were turned 
      to the same intervallic pattern, but a minor third higher: G C F A D G - easily achieved by capo-ing 
      the guitar at the third fret. Note that this is similar to standard guitar tuning in that the 
      strings are mostly tuned a fourth apart (low to high), with one pair set a major third apart. 
      In standard guitar tuning, the third is between the 3rd and 2nd strings (G to B), while in lute 
      tuning, the third is between the 4th and 3rd strings (D to #). This tuning must be very inconvenient 
      for some keys (C major, for example), but it might work nicely for D major, at least. 
      Songwriter-singer-guitarist **Bruce Cockburn** (who calls it 'Drop-# tuning) uses it 
      extensively -- for the keys of D and E, at least.`
    ]
  }, {
    notes: 'Eb Ab C# F# Bb Eb',
    mds: [
      `Used by many players, including **Eddie Van Halen**, on *Jamie's Crying*, **Nick Drake** 
      on *At the Chime of a City Clock*, and **Syd Barrett** on *Octopus*.`
    ]
  }, {
    notes: 'D G C F A D',
    mds: [
      `Used by **Paul McCartney**, for *Yesterday*, **Neil Young**, for *Tell Me Why* 
      and *Sugar Mountain* [correct?!], R.E.M.'s **Peter Buck**, **Elliott Smith**, many others.`,
      `Teenager **John Fogerty** tuned his Harmony 12-string acoustic guitar down a whole step,
      after hearing **Pete Seeger** advise it at a concert, and later bought a Gibson ES-175 electric 
      six-string specifically to keep in down-a-whole-step tuning, which he used for Creedence Clearwater 
      Revival's *Proud Mary*. After his ES-175 was stolen, Fogerty used a Gibson Les Paul, also tuned 
      down a whole step, for hits including *Bad Moon Rising*, *Heard it Through the Grapevine*,
      and *Fortunate Son*. This lowered tuning allowed Forgety to sing in the comfortable keys of D and G,
      while using the more rock-worthy chords for the keys of E and A, respectively. In the case 
      of *Fortunate Son*, he says, "Even though it's in the key of G, I'm playing open [sic] A chords. 
      That's what gives it that sound, especially when I go to the V: I'm hitting a big D chord [E chord 
      grip, with deep root at open string 6], rather than a cowboy-chord D [with root on string 4] in 
      regular tuning." - Acoustic Guitar magazine, January 2010`,
      `Old 97's bass player **Murry Hammond** reports, "Just for the record, we tune everything down 
      a full step. E's becomes D's, A becomes G, D becomes C, etc. Heck, If we had a 'K' we, notes: 'D 
      probably drop it down to a 'J.' This tuning down mess is mine and Rhett's [vocalist **Rhett Miller**] 
      fault. Before 97's started rehearsing, Rhett and I were kicking around a lot of old Sleepy Hero 
      songs and found that some of the notes were too high to hit comfortably, so we tuned everything 
      down a whole note. It's been like that ever since."`,
      `The esteemed but sinister **Dimebag Darrell** (R.I.P.) reportedly tuned his Washburn 
      Stealth "Dimebag" model down 1-1/4 steps.  - Guitar World magazine, April 2001.`
    ]
  }, {
    notes: 'C G D A E G',
    photos: [{name: 'Robert_Fripp.jpg', width: 225}],
    mds: [
      `Extends the range of the guitar by two full steps in the bottom, and one-and-a-half steps 
      at the top. The strings are tuned in fifths -- like the violin family -- rather than in fourths,
      with the exception of the 1st string, which is a minor 3rd above the 2nd string. The 2nd and 1st 
      strings must have to be light gauge, and the 6th string must have to be quite heavy.`
    ]
  }, /** DOUBLE {
    notes: 'C G C F A D',
    mds: ['Used by **Killswitch Engage**, for all but a few of their songs. ']
  }, */ {
    notes: 'C G C D G A',
    photos: [{name: 'Martin_Carthy.gif', width: 180}],
    mds: [
      `It could be described as a C6add9 chord, but this is probably no more significant than naming 
      the open chord that standard tuning produces (Em7add4).`,
      `"At some point I decided to mostly focus on one tuning so that I wasn't having to relearn the 
      instrument with every new tuning. So I have my own standard tuning: CGCDGA. - Article *TRADDAD* 
      by **Duck Baker**, in Acoustic Guitar magazine, July 2005`,
      `Carthy told me that he uses it for playing in D, especially (!), and also C, G, Em and Am and 
      at least one other key. He told me that heavy gauge strings are a necessity - for the 1st, 2nd, and 
      3rd strings, at least, and the 6th string too, I'll bet. I believe that all of the strings are 
      lowered, none of them raised -- which appears to be supported by Teja Gerken's explainer in the 
      July 2005 edition of Acoustic Guitar magazine that Carthy uses custom string sets to accommodate 
      this tuning, with "typical gauges" of (high to low): .015, .018. .027, .039, .049, .059. Note the 
      especially heavy 1st string, which is dropped a fifth to A. The .015 gauge string Carthy uses 
      would more typically be used as a 2nd string.`,
      `When I asked Carthy if he had any reservations about other people learning the details of his 
      tuning he said, "Oh no... feel free to use it! - Conversation with **Martin Carthy**, during 
      break at 20 Apr 1996 concert, La Jolla, CA.`
    ]
  }, {
    notes: 'B E A D F# B',
    mds: [
      `This is the typical tuning for long-scale (27" to 29-3/4") baritone guitars. **Ralph Towner** 
      uses this tuning on his acoustic baritone guitar on his 2010 album *Chiaroscuro*.`,
      `Also used on a standard-scale B.C. Rich 6-string electric by Coal Chamber's Meegs Rascom ' , who 
      says, "...it's  tuned like a seven-string... but I have something called a Hip Shot on the B 
      string [6th string] that lets me detune it to an A. I also use really heavy strings. I have a .70 for 
      the low B string. The whole guitar just sounds fatter, ballsier."`
    ]
  }, {
    notes: 'C C D G A D',
    mds: [
      `Used by **Michael Hedges**, for  *Aerial Boundaries*. String 6 down to C,
      and string 5 up to *C*. The open 2 (D) on string 1 echoes the hammer-on-the-3
      (E) opportunity present on string 4 (as in standard tuning).`,
      `Tuning string 5 up a minor third to C will substantially increase the tension
      pulling across the guitar's top. Don't leave your guitar in this tuning for any
      longer than necessary. `
    ]
  }, {
    notes: 'C C C G C E',
    photos: [{name: 'Bob_Dylan.jpg', width: 108}],
    mds: [`Used by **John Fahey**, for
      *Dance of the Inhabitants of the Palace of King Phillip the 14th of Spain*.`,
      `String 6 down to C, string 5 *up* to C, string 4 down to C. -
      per **Pat Missin**. Tuning string 6 up a minor third to C will substantially
      increase the tension pulling across the top of the guitar. Don't leave your
      guitar in this tuning for any longer than necessary.`
    ]
  }, {
    notes: 'C C G C C C',
    mds: [
      'Invented by Crimean Tatar tapping virtuoso **Enver Izmaylov**. ',
      `"I use these tunings to help convey the characteristics of the [Crimean Tatar] 
      folk music I explore." Izmaylov's other tuning invention is E B E E B E. - 
      article in Guitar Player magazine, July, 2010.`
    ]
  }, {
    notes: 'C A D G B E',
    mds: [
      `6th string down to C, and everything else as in Standard tuning. Used by **Bob Dylan**
      for *It's All Over Now, Baby Blue*.- Acoustic Guitar magazine, Dec 2000`
    ]
  }, { 
    notes: 'C A C G C E',
    photos: [{name: 'Jimmy_Page.jpg', width: 185}],
    mds: [
      `"It's a C tuning, but it's not a *C* tuning [not an open C tuning]. 
      I made it up. It's [thinks., long pause], from low to high, E, C, G, C, A, C. 
      I used that on *Bron-Yr-Aur Stomp*." - **Jimmmy Page**, Guitar Legends magazine,
      Fall 1993.`,
      `At the urging of **Derek DePrator**, who writes "The reason the **Jimmy Page** tuning 
      makes no sense is because you have it backwards."... I have inverted Jimmy's 
      recollection of this tuning to the much more likely (low to high) C, A, C, G, C, E. 
      String 6 would be tuned down to C (after replacement with a heavier guage). 
      String 5 stays put, string 4 drops a whole step to C, string 3 stays as is, string 
      2 comes up a half step to C, and string 1 holds steady. The A of string 5 may seem 
      out of place in a C tuning (being the 6) but it affords a low tonic bass note for 
      the potentially important relative minor chord: Am.`
    ]
  }, { 
    notes: 'C G E G C E',
    mds: ['Noted by **Pat Missin**.']
  }, { 
    notes: 'C G D C G D',
    mds: [
      `A favorite of **Sonic Youth**, used for *Pattern Recognition*,
      *Dude Ranch Nurse*, and *I Love You, Golden Blue*. - article in Guitar 
      Player magazine, Sept 2004.`,
      `All strings tuned *down* to their respective pitches, probably.`
    ]
  }, { 
    notes: 'C G D G C D',
    mds: [
      `Used by **Martin Simpson**, **Seth Austin**, **Tony McManus**,
      **Chris Proctor**, **Anton Emery**, **Joni Mitchell**, and **Sonic Youth**. 
      Name suggested by **Steve Baughman**, after the Orkney Islands off the northern 
      coast of Scotland.`,
      `String 6 is dropped way back to C (after replacement with a heavier gauge string). 
      String 5 is lowered a whole step to G, strings 4 and 3 are left as is, string 2 is 
      raised a half step to C, and string 1 is lowered a whole step to D.`,
      `While many alternate tunings are more or less married to a particular key,
      Orkney works well for at least the keys of G and C, and **Steve Baughman** also 
      uses it for the key of D, and it may be usable for Cm as well. This flexibility 
      owes much to open bass strings set to C, G and D respectively (rather than to a 1,
      a 5, and an octave of the 1, as in many other tunings).`,
      `DADGAD players note: many chords and other fingerings are directly transportable 
      to Orkney tuning, because the intervals between Orkney's strings 1-5 are the same 
      as between DADGAD's strings 2-6.`,
      `As explained by **Steve Baughman**, "It is a wonderful tool for melodic (non-linear) 
      playing, in which you avoid playing subsequent notes on the same string. 
      This technique (**Pierre Bensusan** calls it "harp style") allows for a very 
      smooth and gentle delivery of the melody and avoids the staccato effect that marks 
      many guitar arrangements of fiddle tunes."`,
      `In an e-mail message, **Baughman** further disclosed that, "It is also becoming 
      a tuning used by singer-songwriters as accompaniment." - article *Orkney Tuning* 
      by **Steve Baughman**, Acoustic Guitar magazine, Dec 2008 `
    ]
  }, {
    notes: 'C G D G C E',
    mds: [
      `Used by **Michael Hedges**, for  *Spring Buds*. Good bass support for the V
      chord (G). Drop strings 6 and 5 *down* to target pitches, and raise string 2 up a
      half-step to C.`
    ]
  }, {
    notes: 'C G D G B E',
    photos: [{name: 'Richard_Thompson.jpg'}],
    mds: [
      `Used by **Duck Baker**, by **Sergio Assad** (who describes it as "like a cello"),
      and by **John Leventhal**, who notes, "You keep the top four strings the same so you 
      can sort of voice chords the way you're familiar with doing it, and then it gives you 
      a lot of rich root-fifth possibilities underneath it."`,
      `Used by **Richard Thompson**, who says, "Modal tunings such as C G D G B E 
      low-to-high, also extend the possibilities of the guitar by giving you a richer 
      bass sound, and making it almost like having three keys available at once with 
      just a very slight adjustment of your fingers."`,
      `Used by **Andreas Kapsalis** who says, "The .060 gauge sixth string still sounds 
      good when tuned down to C. In fact I'll often tune the sixth and firth strings down 
      to B and #."`,
      `Good bass support for the V chord (G). Works well also for keys of G and D, if we 
      understand Thompson's statement correctly. Drop strings 6 and 5 *down* to target 
      pitches, and leave the other four strings as is.`
    ]
  }, /** DOUBLE { 
    notes: 'C G D G A D',
    photos: [{name: 'Kelly_Joe_Phelps.jpg', width: 117}],
    mds: [
      `This tuning can be thought of as DADGAD with the 5th and 6th strings dropped yet 
      another whole step. Used by **Lawrence Juber**, who sometimes switches to it 
      from DADGAD to play a song in C. "It works well for the keys of C, C minor, G, and 
      G minor."  Lawrence Juber, Fingerstyle Guitar magazine, Oct 2003.`
    ]
  }, */ { 
    notes: 'C G D F C E',
    mds: [
      `Invented by **Joni Mitchell**, for *Sistowbell Lane*. 
      Drop strings 6, 5 and 3 *down* to target pitches, and raise string 2 a half-step 
      to C.`
    ]
  }, { 
    notes: 'C G C G C F',
    mds: [
      `Used by **Kelly Joe Phelps**. "I like to tune the guitar to C G C G C F because 
      the tuning functions much like DADGAD. It's that same roots and fifths thing with 
      a fourth thrown in."  **Kelly Joe Phelps**, Guitar Player magazine, Oct 2001.`,
      `Tune strings 1 and 2 up a half step. Tune strings 4, 5, and 6 down.`
    ]
  }, { 
    notes: 'C G C G C E',
    photos: [
      {name: 'John_Fahey.jpg', width: 244},
      {name: 'Bill_Frisell.jpg', width: 135}
    ],
    mds: [ 
      `This tuning has been widely used, per **Pat Missin**, who calls it 'standard 
      C tuning', and points to **John Fahey**: *Revolt of the Dyke Brigade*,
      *Requiem for John Hurt*, *Sunflower River blues*, and *Banks of the Owchita*... 
      and also **Leo Kottke**, for *Busted Bicycle*  and *Watermelon*. Also used 
      by **Bob Brozman**, for *C Stomp Blues* on his album 'Blue Hula Stomp' - 
      e-mail exchange with **Pat Missin**, May 2007.`,
      `Employed also by **Bill Frisell**, who wrote the tune *Brother* while 
      experimenting with this tuning. "...the guitar would ring with these wonderful 
      overtones." - Acoustic Guitar magazine, Sept 1997`
    ]
  }, { 
    notes: 'C G C G C Eb',
    mds: [
      `Used by **Bob Brozman** for *Agogostein's Lament*. 
      Also used by **Charles Saufley**. - e-mail exchange with **Pat Missin**, May 2007.`
    ]
  }, {
    notes: 'C G C G C D',
    mds: [
      `"**Nic Jones** used to play beautifully in in Csus2 tuning. 
      That's equivalent to double-C tuning on the five-string banjo." - **Martin Simpson**,
      in Acoustic Guitar magazine, March 2004.`
    ]
  }, {
    notes: 'C G C G C C',
    mds: [
      `Variant of C G C G C E open C tuning used by **John Fahey** for *Requia* - 
      **Arvid Burman Smith Jr**, in his book *Contemporary Slide Guitar* '. **Pat Missin** 
      says that **Fahey** probably also used it for *When the Catfish Is In Bloom*.`
    ]
  }, {
    notes: 'C G C F C F',
    mds: [
      `Used by **Nick Drake**, for *From the Morning*. This tuning has the same 'layout' 
      as DADGDG, but down a whole step.`
    ]
  }, { 
    notes: 'C G C F C E',
    mds: [
      `Used by **Nick Drake**, for *River Man*, *Time Has Told Me*, *Ride*. 
      This tuning has the same 'layout' as DADGDF#, but down a whole step. `
    ]
  }, {
    notes: 'C G C F C D',
    mds: [
      'Invented by **Martin Simpson**, who uses it especially for slide playing.'
    ]
  }, { 
    notes: 'C G C F A D',
    mds: [
      `This tuning is not as strange as it looks. It's the same 'layout' as Drop-D,
      with all strings tuned one full step lower, so the relationships between the 
      strings (except for the 5th and 6th) are the same as in standard tuning. Used by 
      **Joseph Spence**, **Neil Young**, **Leo Kottke** - for the studio version 
      of *Louise*, which **Kottke** has also performed live, tuned half a step lower 
      yet - in Drop-Bb... not on light gauge string, I expect. Used almost exclusively 
      by Orange County rockers Atreyu.`
    ]
  }, {
    notes: 'C G C F G B',
    mds: [
      `Used by **Michael Hedges**, for *Aura Muunta*. All strings *down* 
      to target pitches, very likely. Great support for the V7 chord (G7: G B D F).`
    ]
  }, /** DOUBLE  { 
    notes: 'C G G D G D',
    mds: [
      `Used by **Nels Cline**, for *Grow Closer* and *Red Line to Greenland*. 
      Tune each string *down* to target pitches, very likely. This tuning would also be 
      very much at home in the key of G, with the low C string supporting the IV chord. `
    ]
  }, */ {
    notes: 'C F C G A E',
    mds: [
      `Used by **Michael Hedges**, for  *The Magic Farmer*. All strings tuned *down* 
      to target pitches, surely. This tuning appears to be arranged with the IV chord 
      in mind: F (F A C), as well as the relative minor key's I chord: Am (A C E)`
    ]
  }, { 
    notes: 'C F C C G C',
    mds: [
      `Used by **Michael Hedges**, for *Shell Shock Venus*. All strings tuned *down* 
      to target pitches, probably. The lowest three strings might appear to suggest the 
      key of F, but the F string is probably for the benefit of the IV chord. The tuning 
      could be named "Csus4" but the 4 has no opportunity to "un-suspend" to the 3. It's 
      probably best understood as the 1 of the IV chord, rather than the 4 of the I chord... 
      which indicates how insignificant is the chord described by the open strings of most 
      altered tunings (unlike the truly 'open' tunings, where the chord name really does 
      mean something).`
    ]
  }, {
    notes: 'Eb Ab D G B E',
    mds: ['Used by **Sergio Assad**, specifically for Ab.']
  }, {
    notes: 'C# G# D# E G# D#',
    mds: [
      `**Michael Hedges**, for *Theme from Hatari!*.`,
      `The C# for string 6 would correspond to the IV chord. All strings tuned *down*,
      very likely.`
    ]
  }, {
    notes: 'E A E G B E',
    mds: [
      `Used by **Nick Drake**, on *One of These Things First* (capo-ed 4th fret...
      played as if in A, sounds in C#).`
    ]
  }, {
    notes: 'E A D G A E',
    photos: [{name: 'Steve_Gillette.jpg', width: 218}],
    mds: [`Used by **Steve Gillette**, **Mary McCaslin** ...`,
      `"I learned this wonderful tuning from **Steve Gillette** way back in the 1960's. 
      Over the years I've used it for lots of songs, it's a favorite." - **Mary McCaslin** `,
      `This is a 1/6th tweak on standard tuning: the 2nd string is lowered from B to A,
      opening up an opportunity for a 3-less modal-sounding A 'chord' with the 2nd string 
      ringing open. As with standard tuning, great for hammering-on 5 on top of 4 (on 4th 
      string) and 1 on top of flatted 7 (on 3rd string).`,
      `Together with the omission of the 3, this points toward the pentatonic pipe-tune 
      scale: 1, 2, 4, 5, b7 (a, b, d, e, g).`,
      `In any case, this tuning is great for playing melodic lines (in A, especially) on 
      the 3rd string, or intervals on the 3rd and 4th strings together, against the open 
      A and E of the 2nd and 1st strings (not to mention the open A and E of the 5th and 
      6th strings).`
    ]
  }, {
    notes: 'E A D G A D',
    photos: [{name: 'Mary_McCaslin.gif', width: 135}],
    mds: ['Used by **Sarah McLachlan**, **Lisa Loeb**, **Ani DiFranco** ...',
      `There was a chord voicing that **Sarah McLachlan** showed me in an open [sic] 
      tuning, E,A,D,G,A,D. I showed it to **Dweezil** [**Zappa**, her boyfriend], and 
      he came up with some cool instrumental things. I took that music, rearranged it 
      a little and added lyrics, and then we finished up the song [*Underdog*] 
      together." - **Lisa Loeb**, article in Aug 2002 *Acoustic Guitar* magazine`,
      `Similar to the A tuning above, but with also the first string altered from 
      standard -- down a full step to D. Despite its kinship to DADGAD -- the same 
      except for the 6th string held at E -- this tuning may be best suited to the 
      key of A.`
    ]
  }, {
    notes: 'E A D G A C',
    mds: [`**Michael Hedges**, for *Road Music*. Only strings 2 and 1 are retuned -- 
      both *lowered* to their pitches.`
    ]
  }, {
    notes: 'E A D E A E',
    mds: ['**Martin Carthy**, **Davey Graham**, **Pat Kirtley**...',
      `"I use a pipe tuning for playing the Irish pieces. The 2nd string is tuned down 
      to A, and the 3rd string is tuned down a tone and a half to E. **Martin Carthy** 
      taught me the tuning." - **Davey Graham**, British Fingerpicking Guitar,
      **Stephan Grossman**, 1976.`,
      `"My very favorite tuning, EADEAE, is the hardest of the bunch [of the more 
      difficult tunings] to comprehend. Only two strings are changed from standard,
      but they are the 2nd and 3rd strings, and they are changed by 1 and 1-1/2 steps 
      down, respectively... resulting in the total brain-death of your accumulated 
      chord and scale knowledge. Ironically... it is where I've had the greatest 
      productivity as a composer and arranger; I have written or arranged about 40 
      tunes in EADEAE. It is at its best in the key of A, and contrary to expectations,
      it is just about useless in E!" - article *Opening Up to Open Tunings*,
      **Pat Kirtley**, *Acoustic Guitar*, 1999.`
    ]
  }, {
    notes: 'E A E A C# E',
    photos: [{name: 'Robert_Johnson.gif', width: 266}],
    mds: [
      `Used by **Robert Johnson**, for his *Cross Road Blues*, and *Terraplane Blues* 
      (capo-ed up 2 to sound in B).`,
      `"This high-pitched approach to a tuning commonly used in country blues complemented 
      Johnson's voice and lent a taut, searing quality to his upper-register bottleneck 
      figures." - Column *Off the Record*, *Acoustic Guitar*, Aug 2003.`,
      `Used by **John Lee Hooker**. "Hooker played in several tunings, but favored open 
      A (E A E A C# E) for his boogies." [like *Boom Boom* and *Boogie Chillun*]. - 
      article *How to play like... John Lee Hooker*, by **Andy Ellis**, *Guitar Player*,
      Jan 2005.`,
      `It may be convenient to think of this tuning as a ratcheted-up version of Open G 
      tuning, with all of the strings tuned up one full step higher. Relative to standard 
      tuning, the 6th and 5th strings stay the same, the 4th, 3rd and 2nd strings are each 
      pushed up a full step to E, A, and C#, respectively, and the 1st string remains at E.`,
      `The extra tension of the three raised strings may put an undue strain on your 
      guitar's neck, unless your strings are relatively light, therefore this tuning may 
      be best suited to electric guitars, where the strings tend to be lighter.`
    ]
  }, {
    notes: 'D A C# E A E',
    mds: [
      `Invented by **Cheryl Wheeler**, searching for something functionally 
      equivalent to DADGAD for the key of A. Used by her for *Rainy Road Into Atlanta,
      If It Were Up To Me*, and several other songs.`,
      `The 6th tuned to D  makes possible a huge IV chord: 001200, offering the ever-welcome 
      2 to 3 hammer-on (e to #) on the 3rd string. For an A major triad, she frets the 6th 
      string at the 2nd fret and plays the rest of the strings open: 200000. - 
      Acoustic Guitar magazine, Oct 1999.`,
      `Also used by **Patty Larkin** on her 2003 album *Red=Luck*.`
    ]
  }, {
    notes: 'E A E A C E',
    mds: []
  }, {
    notes: 'C G E E B E',
    mds: [
      `Used by **Joni Mitchell**, for *Help Me* [?!] Hmmm.... should we refile this 
      one under C, as Cmaj7... or should we imagine Joni fretting string 5, fret 2 for 
      a big Am9...? `
    ]
  }, {
    notes: 'A A D A A C#',
    mds: ['Used by **Nick Drake**, for *Hanging on a Star*. ']
  }, {
    notes: 'A E A D F# A',
    mds: [
      `Used by **Steve White** for many songs, including *Faial*. As White explains,`,
      `"Now I play in double drop D tuning (DADGBD), 5 half steps below standard." 
      In other words, all strings tuned down a fourth, to a baritone-range edition 
      of standard tuning, and then strings 1 and 6 dropped another whole step -- 
      yielding a fourths-down version of Double-Drop D, which could be thought of 
      as Double-Drop A. White further notes, "I've been tuning my guitar lower and lower 
      and use especially heavy strings: 017 to 68. The string gauge compensates for the 
      low tension."`,
      `Strings this heavy must be necessary for intonation and playability. **White** must 
      have strong hands to wield such massive strings, even though the tension must be 
      relatively low, considering how far down they're tuned.`
    ]
  }, {
    notes: 'F Bb C F A D',
    photos: [{name: 'Keola_Beamer.jpg', width: 216}],
    mds: [`**Keola Beamer** - *Namakelua's Tune*. 6th string up to F, 5th string up to 
    Bb, other strings dropped one whole step each. This one must be meant for Bb, given 
    the strings tuned to F (the 5) and D (the 3). The C on the 4th string is the 2,
    facilitating internal resolves to the 3. (D) It's very unusual to include the 7 (A) 
    in a tuning, though it makes sense as part of the V7 chord (F7), and would enable 
    hammered-on resolutions to the Bb tonic. This is one of the many Hawaiian slack-key 
    tunings.`]
  }, {
    notes: 'Bb F C Eb F Bb',
    mds: [`**Michael Hedges**, for *I Carry Your Heart*.`,
    `All strings *lowered* to their pitches, most likely.`]
  }, {
    notes: 'Bb F Bb C F Bb',
    mds: [`**Michael Hedges**, for *Baal T'Shuvah*. All strings *lowered* to their 
    pitches, probably.`]
  }, {
    notes: 'D B B F# B E',
    mds: ['Used by **Andreas Kapsalsis**, for the key of Bm, presumably... ']
  }, {
    notes: 'B F# D G B E',
    mds: [`Used by **Andreas Kapsalis**, who says, "The .060 guage sixth string still 
    sounds good when tuned down to C. In fact I'll often tune the sixth and firth strings 
    down to B and #." `,
    `The top four strings remain in standard tuning. String four's D (b3) and string 
    three's G (b6) strongly suggest the key of B minor.`]
  }, {
    notes: 'B F# C# F# F# F#',
    mds: [` Used by **Michael Hedges**, for *Follow Through*.`,
    `All strings tuned *down*, very likely, except for string 1 -- up a full step. `]
  }, {
    notes: 'B F# C# F# F# B',
    mds: [`Used by **Michael Hedges**, for *Dream Beach*.`,
    `All strings tuned *down*, very likely.`]
  }, {
    notes: 'B F# C# E A A',
    mds: [`Used by **Michael Hedges**, for *Pinball Wizard*.`,
    `All strings tuned *down*, very likely.`]
  }, {
    notes: 'B F# C# D A D',
    mds: [`Used by **Michael Hedges**, for *After the Gold Rush*, *Dirge*,
    *Face Yourself*, *Menage a Trois*. All strings tuned *down*, very likely.`]
  }, {
    notes: 'B F# B F# B D#',
    photos: [{name: 'Tom_Rush.jpg', width: 132}],
    mds: [`Used by **Tom Rush** for *Rockport Sunday*[?], *Long John*[?],
    *Urge for Going*[?]. 6th string *down* to B. 5th string *down* to #.`]
  }, {
    notes: 'B F# B E B D#',
    mds: ['Used by **Nick Drake**, for *Which Will*. ']
  }, {
    notes: 'D A D G A D',
    photos: [
      {name: 'Davey_Graham.jpg', width: 236},
      {},{},{},{},{},{},{},{},{},{},{},{},{},{},{},
      {name: 'Bensusan01.jpg', width: 295}],
    mds: [`Owing to its 'pronounceability', DADGAD enjoys the very rare distinction 
    of being a word, as well as a tuning.`,
    `Used by a great multitude of players, including **Davey Graham**, **Pierre Bensusan**,
    **Lawrence Juber**, **John Renbourn**, **Michael Hedges** (*Peg Leg Speed King*),
    **Bert Jansch**, **Peppino D'Agostino**, **Martin Simpson**, **Jimmy Page** 
    (*Black Mountain Side*, *Kashmir*, others), **Nick Drake** (*Three Hours*),
    **Stefan Grossman** (*Woman From Donori*), **John Hoskinson** (*Uncharacteristic*)...`,
    `The invention of DADGAD is generally attributed to the enigmatic and English 
    **Davey Graham** (1940-2008). "**Graham** invented the DADGAD tuning in order to 
    play Eastern and Arabic music on the guitar." - **Colin Harper**, article 
    *In the Moment*, Acoustic Guitar magazine, Sept-Oct 1993.`,
    `"**Graham** came up with the DADGAD tuning while accompanying oud players in 
    Morocco." - **Mark Hanson**, in *The Alternate Tunings Guide for Guitar*, 1991. `,
    `I asked **Pierre Bensusan**, at one of his workshops, if his understanding was 
    that **Graham** was the inventor of DADGAD. He nodded affirmatively, adding,
    "Well, that's what they say." `,
    `Other sources indicate that it's not entirely 
    clear whether or not **Graham** originated the tuning, though it is at least 
    well-established that he introduced it to British guitarists including 
    **Bert Jansch** and **Martin Carthy**, in the early 1960s. `,
    `Recently, **Martin Carthy** had this to say about it:
    "There was no question that **Davey Graham** was 'the man'. 
    He led the way and everyone else came later. He played absolutely wonderfully,
    but he also allowed you to play. ...he was ahead of all of us and we were all 
    following where he went, and then he came up with DADGAD." and "We were all 
    messing around with tunings already, all the guitar players were. For me it 
    was part of searching for a way to play English folk songs in a way that was 
    appropriate to the music, so that the guitar would seem natural and not like 
    an interloper. I started looking around for a tuning that could give me what 
    I was hearing, and I came up with DGDGAD. The suspension was right, but it 
    wasn't quite there until **Davey** turned up at the Troubadour [Earl's Court, London] 
    one night and played *'She Moves Through the Fair'* in DADGAD." - **Duck Baker**,
    article *TRADDAD*, Acoustic Guitar magazine, July 2005.`,
    `DADGAD can be seen as one step progressively further than Double Drop-D, on a 
    vector dropping below standard tuning: the 2nd string is also lowered a full step,
    to A. This brings the 1st and 2nd strings back into the familiar interval of a 
    fourth apart, convenient for melody playing. The very odd thing is that the 
    interval from the 3rd string to the 2nd string is just a major second - the g 
    and a notes will be adjacent scale tones in most any key for which one would 
    use DADGAD.`,
    `**Lawrence Juber** explains how this opens up an alternative approach to melody 
    playing, wherein combinations of stopped and open strings yield successive scale 
    tones on adjacent strings, which allows one to rapidly play arpeggiated 'cascading' 
    melody lines, full of ringing open string tones. He demonstrates with the D major 
    scale: fret zero (open) of 1st string (d), fret four of 2nd string(c#), four of 
    3rd (b), zero of 2nd (a), zero of 3rd (g), four of 4th (#), seven of 5th (e),
    zero of 4th (d). Playing the d major scale this way allows up to four notes 
    to ring simultaneously, as if they were played on a piano with the sustain pedal 
    depressed.`,
    `Physically, it's very easy to play, but mentally.... one must overcome the 
    tendency to always reach for the next lower note on either the next lower fret 
    or the next lower string. If you step through the example above, you'll encounter 
    two instances where the next lower note is played on the next higher string...! `,
    `While **Davey Graham** may well have originally conceived DADGAD with the key 
    of D in mind, guitar players have used it for many other keys as well. `,
    `**Lawrence Juber**, for one, employs it for pieces in F, Bb, C, G, A, Am,
    and E, as well as D. "It allows for textures and chord voicings that do not 
    exist in standard tuning. I love DADGAD because it lets me go to these 
    impressionistic sounding voicings."`,
    `"I found that *Strawberry Fields Forever* works best in Bb with DADGAD tuning. 
    Functionally, that was the original key. The original is actually in the crack 
    somewhere between A and Bb because **George Martin** spliced two different 
    versions and altered the tapes's speed. In terms of making a guitar arrangement,
    DADGAD works well -- the Ebmaj7 chord sounds glorious." - **Lawrence Juber**,
    article in Fingerstyle Guitar magazine, Oct 2003`,
    `**Martin Simpson** advises, "They're a very tightly-related family. 
    Basically, D A D G A D is exactly the D equivalent of Gsus4 [D G D G C D] [?],
    and Csus4add9 [C G C F C D] has much more in common with D A D G A D and with 
    Gsus4 than if you just look at it as a series of notes.`,
    `They all have root, fifth, root, fourth, and whatever else is going on. 
    It's very clear when you think about it like that. - Acoustic Guitar magazine,
    March 2004.`,
    `French virtuoso **Pierre Bensusan** may be the greatest exponent of DADGAD today. 
    He plays entire concerts and records entire albums (*Spices and Solilai*) in DADGAD.`,
    `"The best tuning will be a tuning that is not too colored. To me, standard would 
    be one of the very best. DADGAD is another tuning that is not too colored... 
    I mean a tuning that is going to have a third in it which is going to describe 
    a major or minor triad [as in one of the 'open' tunings, like DADFAD (D-F)].
    That's too colored for me. It already implies certain attitudes. DADGAD doesn't. 
    I use DADGAD as my tool. Any key, it doesn't matter." - **Pierre Bensusan**,
    quoted in article *Designing Space*, Acoustic Guitar magazine, Feb, 2002.`,
    `**Martin Simpson** agrees, with Open G [D G D G B D] in mind: "You're always 
    fighting the existence of the major third, and I play a lot of modal music,
    which is outside that. [The major third is between the G and B of strings 
    3 and 2, respectively, and there's also a minor third between the B and D 
    on the highest two strings.] - Acoustic Guitar magazine, March 2004.`,
    `At a workshop he conducted at Shade Tree Stringed Instruments, **Laguna Niguel**,
    CA, in April 2000, I asked **Bensusan** how much of his playing is in DADGAD,
    these days. "What do you think?", he asked back. "95%?". He shook his head,
    "All the time."`]
  }, {
    notes: 'F A D D G D',
    mds: [`Used by **The Edge**.`,
    `"He [U2's **The Edge**] also created unusual tunings, such as the FADDGD heard 
    along with an E-Bow on the title track (*The Unforgettable Fire*)" - Guitar.com,
    mid-2001.`]
  }, {
    notes: 'D A F G A D',
    mds: [`Used by **M. Ward**.`,
    `Strings 6 *down* a whole-step, string 4 *up* a minor third, strings 2 and 
    1 *down* a whole-step.`]
  }, {
    notes: 'D A E F A E',
    mds: [`Used by **Michael Hedges**, for *Oracle*.`,
    `Strings 6, 3, and 2 *down* a whole-step, string 4 *up* a whole-step.`]
  }, {
    notes: 'D A D G D G',
    mds: [`Used by **Nick Drake**, on *Northern Sky*.`,
    `2nd and 1st strings probably 
    both tuned *up* a minor third to D and G, respectively. I don't think I'd want 
    to leave them at those pitches for long, especially with heavier strings.`]
  }, {
    notes: 'D A D G D F#',
    mds: [`Used by **Nick Drake**, on *Introduction* (Bryter Layter), *Pink Moon*,
    *Hazey Jane I*, and *Hazey Jane II* (capo-ed up one half-step).`,
    `2nd string probably tuned up a minor third to D, and 1st string *up* a whole 
    step to #.`]
  }, {
    notes: 'D A D G C E',
    mds: ['Used by **Patty Larkin**, somewhere on her 2003 album *Red=Luck*.']
  }, {
    notes: 'D A D G C Eb',
    mds: [`Used by **Andreas Kapsalis**.`,
    `b9, no less... perhaps in part to facilitate a b7m chord (Cm) on the top four 
    strings... and / or to support Greek and Arabic scales that include a b2... 
    like the Byzantine / Hijaz Kar scale: 1 b2 3 4 5 b6 7.`]
  }, {
    notes: 'D A D G B E',
    photos: [{name: 'Neil_Young.jpg', width: 250}],
    mds: [`There are probably few experienced players who haven't used Drop-D at 
    some time or other.`,
    `A few classic examples: **Jorma Kaukonen** - *Embryonic Journey*,
    **James Taylor** - *Country Road*, **The Beatles** - *Dear Prudence*,
    **Leo Kottke** - *Louise*,
    **Robbie Robertson** - *Jemima Surrender*, **John Denver** - *Rocky Mountain High*,
    **The Doobie Brothers** - *Black Water*, **Ry Cooder** - *FDR in Trinidad*,
    **Jimmy Page** - *Going to California*.`,
    `Drop-D is the most straightforward of all the alternate tunings: simply drop 
    the 6th string one full step, from E to D. Now you have a deep bass d root for 
    your D chords. This tuning is also popular with guitarists backing up fiddle 
    tunes in D, especially the Irish jigs and reels.`]
  }, {
    notes: 'D A D G B D',
    mds: [`Used by **Joni Mitchell** - *Free Man In Paris*, **Buffy St. Marie**,
    **Neil Young** - *The Loner*, *Cinnamon Girl*, *Ohio*...
    **Stephen Stills** - *Bluebird*, enabling that huge Cadd9 chord toward the end,
    just before the banjo part,
    **The Doobie Brothers** - *Black Water*.`,
    `**Billy Gibbons** of **ZZ Top** reports: "This puts a partial D chord on the 
    bottom and a G chord on the top three. I use this on a lot of the slide cuts 
    we do.`]
  }, {
    notes: 'D A D G B C',
    mds: ['Used by **Mary Chapin Carpenter**']
  }, {
    notes: 'D A D G A F#',
    mds: ['Used by **Nick Drake** . Tune the first string *up* to #, presumably.']
  }, {
    notes: 'D A D F# B E',
    mds: [`Used by **M. Ward**.`,
    `Just two strings re-tuned: string 6 down a whole-step, string 3 down a 
    half-step.`,
    `Also used by **Amber Rubarth**. `]
  }, {
    notes: 'D A D F# B D',
    mds: ['Used by **Phil Keaggy**.',
    `String 6 down a whole-step, string 3 down a half-step, string 1 down a 
    whole-step.`]
  }, {
    notes: 'D A D F# A D',
    photos: [{name: 'Ry_Cooder.gif', width: 192}],
    mds: [`Used by **Mississippi Fred McDowell**, **Blind Willie McTell**,
    **Lottie Beamnan**, **Son House**, many other Mississippi Delta bluesmen,
    **Elmore James**, **Bo Diddley**, **Muddy Waters**, **Mary McCaslin**,
    **Joni Mitchell** - *Chelsea Morning* and *Big Yellow Taxi*,
    **Leo Kottke** - *Watermelon*, **Peter Lang**, **David Lindley**, **Ry Cooder** - 
    *Thirteen Question Method*.`,
    `This is the same intervallic pattern as the also-popular Open E tuning,
    but with every string tuned one full step lower, making for less stress 
    on the guitar's neck than imposed by Open E, which requires three strings 
    to be raised in pitch.`,
    `As for Open E, the first-position E7 grip from 
    standard tuning can be used to great effect in its usual position (to 
    add the 6 and sus4 tones to the tonic D), and as a launch point for 
    running 6th intervals on strings 3 and 5, as in this major scale 
    pattern...`,
    `![Fretboard example](/assets/imgs/sebastopol_example.png)`,
    `Since the surrounding open strings sound D, A, D, and D respectively 
    (high to low) -- which are the 1 and 5 notes of the key of D -- they 
    can be incorporated to fill out the sound, and to create all sorts of 
    interesting 'accidental' harmonies.`,
    `Another way to view the pattern of this tuning is as one step beyond 
    DADGAD - with the third string dropped a half-step, in addition to 
    strings 1, 2 and 6 (dropped a whole step).`,
    `**Muddy Waters**: "He (**Son House**) showed me how to tune my guitar 
    in three ways -- natural [standard], Spanish [open G] and 
    cross-note [open D]. - **Muddy Waters** to **Alan Lomax**, reported 
    in an article in Acoustic Guitar magazine, July, 2003.`,
    `"Guitarists have been strumming in open D since the 1800s. 
    Often called *Vestapol* or *Sebastopol* (after a popular 19th-century 
    guitar piece by the same name), this tuning was embraced by blues 
    slide guitarists in the early 1900s. Since then, players of all 
    persuasions -- from **Joni Mitchell** (*Both Sides Now*) to the 
    **Allman Brothers** (*Little Martha*) to **Nirvana** (*Lithium*) to 
    **Adrian Legg** (*The Irish Girl*) -- have drawn inspiration from 
    this handy tuning." - **Andy Ellis** in *Decoding Open D* in Guitar 
    Player magazine, May, 2001.`,
    `**Peter Lang** says, "It's the greatest all-purpose tuning I know of. 
    It's very versatile in that it lets me play really snappy, bluesy stuff,
    while also being magnificent for more complex material. It lets you 
    access some colorful sounds as you work out the melody lines on the 
    higher strings, and also get rich notes out of the bass strings that 
    a lot of other open tunings don't offer." - Interview with **Peter Lang**,
    by **Anil Prasad** in Guitar Player magazine, April, 2008.`,
    `Open D is the tuning that **Bo Diddley** (Ellas Otha Bates / 
    Ellas McDaniel, 1928-2008) used exclusively, if I understand 
    correctly... though with his guitar just roughly at standard pitch,
    and sometimes with a capo...`,
    `According to **Mr. Diddley**, "My sister bought me a guitar,
    and I started beating and banging on it. I finally figured out how 
    to tune it, but I tuned it my way. I liked the way it sounded, and I 
    learned how to finger it, trying to get this and that out of it,
    and finally, I made up some songs of my own. I still tune my guitar 
    the same way. I never did know exactly what key I was tuned to, but I 
    came to find out from some old people that it was called 'Sepastopol' 
    [or Open D, low to high -- D A D F# A D]. It's not hard for other 
    musicians to play with me -- they just have to transpose a bit." - Story 
    by **Jeff Hannusch**, in Guitar Player magazine, June, 1984.`,
    `...and according to **Ry Cooder**, "It's the blues bag. Here you 
    have **Elmore James** and **Blind Willie Johnson** and those kinds 
    of sounds. It's not as bright and strident as Open G -- it's more 
    introspective sounding. Open G wants you to change chords, Open D 
    doesn't care if you change or not. You've got the tonic on the bottom,
    and that's very nice -- you're rooted. In my mind, your blues chops 
    come together in this tuning." - per **Andy Ellis** in *Decoding Open D* 
    in Guitar Player magazine, May, 2001.`]
  }, {
    notes: 'D A D F A D',
    mds: [`Used by **Nehemiah "Skip" James**, **John Cephas**.`,
    `"I think everyone is affected the same way when they hear **Skip James** 
    play... His sound has so much soul and so much eeriness that when you 
    hear it, it amazes you. .. Immediately I said I got to learn what this 
    man is doing. ..finding my way around in the D-minor tuning.. that was 
    all new." - **John Cephas**, in article *Blues Ambassador* Acoustic 
    Guitar magazine, March, 2001.`,
    `"The tuning that James learned form **Henry Stuckey** (E B E G B E) 
    is usually called E minor, but James didn't generally use it to play 
    in minor keys. He would usually fret the third string at the first 
    fret to give the song a major tonality and then use the open string 
    (the minor third) [the b3 - ed.] in conjunction with slides and 
    pull-offs for bluesy melodic runs. On his Paramount session,
    however, he pitched the tuning lower, to D minor. On his 1960s cuts 
    he was closer to Eb minor." - **Orville Johnson Wyatt**, article 
    *Skip James* in Acoustic Guitar magazine, Sept 2010.`,
    `Also used by the **Indigo Girls**.`]
  }, {
    notes: 'D A D F A C',
    mds: [`Used by guest book correspondent "the exhumer", who reports,
    "i like that c.". It's the flatted 7 of D. Maybe he likes to be able 
    to hammer the tonic d on top of it...`]
  }, {
    notes: 'D A D E A D',
    mds: [`Used by **Pat Kirtley**, and **Annie Gallup** (who uses it 
    exclusively). Facilitates hammered-on major third (# on 3rd string),
    which is handy for all sorts of folk-style playing, as well as for 
    country licks. It also places adjacent scale tones on the 3rd and 4th 
    strings, as DADGAD does on the 2nd and 3rd strings. Use the same 
    fingering as DADGAD, oddly enough, for the first position D chord: 
    just press down fret two of the 3rd string.`,
    `With this tuning however, this brings in the natural 3 (#) rather 
    than the the 5 (A).`,
    `Lower the 3rd string down to E. Though it's a drop of three half-steps,
    there's no real tension loss problem, even with light gauge strings.`]
  }, {
    notes: 'D A D D A D',
    mds: [`Used by **Alvin Lee**, **Pat Kirtley** and **Jim Kweskin** for *Chevrolet*.`,
    `String 6 down a whole-step, string 3 way down a fourth, strings 2 and 1 down a whole-step.`]
  }, {
    notes: 'D A C G C F',
    mds: [`Used by **Bruce Cockburn** for his *Down to the Delta*, in Dm. 
    Strings 6 and 4 down a whole-step, strings 2 and 1 *up* a half-step.`]
  }, {
    notes: 'D A C G A D',
    mds: ['Used by **Ani DiFranco**.']
  }, {
    notes: 'D A G G A x',
    mds: [`(4th string *down* to G, no first string) Used by "the exhumer",
    who says, "it might sound kind of eccentric, but i got used to it 
    and can come up with some really FULL sounds. of course, i have been 
    using it for 4 years...". `]
  }, {
    notes: 'D G D G B E',
    preferredNote12: 2,
    photos: [{name: 'Michael_Hedges-02.jpg', width: 200}],
    mds: [`Used, possibly, by bluesman **Lonnie Johnson** (a big influence 
    on **Robert Johnson** and many of his contemporaries). "Almost everything 
    he [**Lonnie Johnson**] recorded is in the key of D. There is a slight 
    controversy over his guitar playing. **Stefan Grossman** thinks he played 
    in Dropped-D tuning, while **Woody Mann** and **Lenny Carlson** think 
    he played in a Dropped D-G tuning (D-G-D-G-B-E). I do not have the answer,
    but both tunings seem to work." - **Olav Torvund** `,
    `Drop strings 6, 5, and 1 a full tone each. One advantage of this 
    Drop-D-G tuning is that the open G on the 5th string affords a 
    convenient low open root for the IV chord of the key of D.... at 
    the expense of what would otherwise be a deep open root for the V 
    chord. Still, one can always hold down the A at the 2nd fret...`]
  }, {
    notes: 'D F C G A D',
    mds: [`Used by **Michael Hedges**, for *Ignition*.`,
    `Only string 3 is left as is; strings 6, 4, 2, and 1 *down* a whole-step,
    string 5 *down* a major third.`]
  }, {
    notes: 'D D D A D F#',
    mds: [`Invented by **George Harrison**, and used by **Peter Frampton** 
    (who learned it from George) on *Winds of Change*.`,
    `Strings 6 down a whole-step to D, string 5 down a 5th to D [requires 
    heavier string], string 3 up a whole-step to A, string 2 up a minor 3rd 
    to D, string 1 up a whole-step to #.`]
  }, {
    notes: 'A A D F# A A',
    mds: [`Used by **Michael Hedges**, for *Gospel* and *Tomorrow Never Knows*.`,
    `Strings 6, 3, 2, and 1 all lowered to their target pitches, almost surely.`]
  }, {
    notes: 'Bb Eb Bb Bb Eb Gb',
    mds: [`**Michael Hedges**, for *Like a Rolling Stone*. `,
    `String 3 might be tuned up a minor third to Bb, and string 1 is very likely 
    tuned up a major second to Gb. Other strings are tuned down, most likely.`]
  }, {
    notes: 'E B E A B E',
    mds: [`Used by **John Sherman**. One full step up from DADGAD. One finger on 
    string 3, fret 2 yields a big E5 chord.`
    ]
  }, {
    notes: 'E B E G# B E',
    mds: [`Used by **Robert Johnson** - *Preachin' Blues*, **Joni Mitchell**, **Tom Rush**,
    **Syd Barrett**, **The Allman Brothers** - *Little Martha*. **Duane Allman's** favorite 
    slide tuning. - **Keith Wyatt**, article *Touch of Gold* in Guitar World Acoustic,
    Spring 2000.`,
    `The 4th and 5th strings go up a full step, and the 3rd string is raised a half step,
    to the major third. The pattern is the same as the most common open D tuning (DADF#AD),
    but a full step higher.`
    ]
  }, {
    notes: 'E B E G B E',
    photos: [{name: 'Skip_James.jpg', width: 160}],
    mds: [`Used by **Bukka White**, **Skip James**, and, more recently, **David Gilmour** 
    (lap steel on *One of These Days*).`,
    `This tuning is most closely identified with Delta bluesman **Nehemiah "Skip" James** 
    (1902-1969). `,
    ` "One of the guitarists James played with regularly during his youth,
    **Henry Stuckey**, showed him an open tuning he'd allegedly picked up from some black 
    Bahamian soldiers while serving in France during WWI. Spelled E,B,E,G,B,E, this E 
    minor "cross-note" tuning, as James called it, became the basis for the majority of 
    his compositions." -  review of James' album*Studio Sessions: *Rare and Unreleased* 
    in Guitar Player, May 2003.`,
    `"The tuning that James learned form **Henry Stuckey** (E B E G B E) is usually 
    called E minor, but James didn't generally use it to play in minor keys. He would 
    usually fret the third string at the first fret to give the song a major tonality 
    and then use the open string (the minor third) [the b3 - ed.] in conjunction with 
    slides and pull-offs for bluesy melodic runs. On his Paramount session, however,
    he pitched the tuning lower, to D minor. On his 1960s cuts he was closer to Eb minor." - 
    **Orville Johnson Wyatt**, article *Skip James* in Acoustic Guitar magazine, Sept 2010.`,
    `(James also played in open G 'Spanish Tuning' (*Special Rider Blues*). He may not 
    have used standard tuning at all. Even his Cream-popularized song *I'm So Glad* was 
    set in his E minor tuning, per **Orville Johnson Wyatt**, in the article noted above. 
    **Eric Clapton** re-arranged it for standard tuning, key of E major, apparently.)`,
    `Tune the 4th and 5th and strings up a full step; the other strings remain as in 
    standard tuning.`
    ]
  }, {
    notes: 'E B E E B E',
    mds: [`Invented by Crimean Tatar tapping virtuoso **Enver Izmaylov**.`,
    ` "I use these tunings to help convey the characteristics of the [Crimean Tatar] 
    folk music I explore." **Izmaylov's** other tuning invention is C C G C C C. - article 
    in Guitar Player magazine, July, 2010.`
    ]
  }, {
    notes: 'E B D G# B E',
    mds: [`used by **M.Ward**`,
    `Just two strings re-tuned: string 5 up a whole-step, string 3 up a half-step.`
    ]
  }, {
    notes: 'E B D G# B D',
    mds: [`**Phil Keaggy**. Note the convenient flatted 7s. String 5 up a whole-step,
    string 3 up a half-step, string 1 down a whole-step.`
    ]
  }, {
    notes: 'E B D G A D',
    photos: [{name: 'David_Crosby.jpg', width: 102}],
    mds: [`**David Crosby**. Used for the **Crosby, Stills and Nash** epic *Guinnevere* 
    (Crosby's spelling) as well as other **Crosby** songs including *Déjà Vu* and 
    *Song With No Words*.`,
    `Two b7s poised to become 8s, and a 4 willing to experience 5-ness. It's could be 
    thought of as a key-of-E-specific DADGAD.`
    ]
  }, {
    notes: 'E B B G B D',
    mds: [`Used by **Ani DiFranco**`]
  }, { 
    notes: 'E A D F# B E',
    preferredNote12: 4,
    mds: [`**Nick Drake**, on *Cello Song* (capo-ed 6th fret... played as if in E,
    sounds in Bb. Nick holds 022000 as a Iadd9 ' chord.), and on *The Thoughts of Mary Jane*.`]	
  }, {
    notes: 'E A C# G B E',
    mds: [`Invented by <a href="http://willbrady.com/" target="_blank">Will Brady</a>
    of Laguna Beach, CA. to support the VIm chord -- C#m -- with an open-string root bass 
    note. The VIm chord is very often encountered in pop songs, and is sometimes the only 
    other chord employed beyond I, IV and V7 (*Oh, Donna*, *Under the Boardwalk*...).`,
    `This tuning suits Brady's thoughtful and ringing fingerstyle approach very well, as he 
    typically teases the melody out of the first three strings (often via 6ths) against 
    sustained bass notes, often entirely from open strings. From Standard tuning, simply 
    tune string 4 down a half-step -- from d to c#.`]
  }, {
    notes: 'E G# E F# B B',
    mds: [`**Michael Hedges**, for *Gospel* and *Out on the Parkway*. String 5 *down* a 
    half-step to G# surely, string 4 *up* a whole-step to E, string 3 *down* a half-step 
    to #, and string 1 almost surely *down* a fourth to B.`]
  }, {
    notes: 'E G# B G# B E',
    mds: [`Used for **Manolo Escobar's** 'Chopi' for classical guitar, as reported by 
    classical player **David Russel**, in Acoustic Guitar magazine, Jan 2004. String 4 down 
    to G#, I suppose... with a heavier string, very likely.`]
  }, {
    notes: 'E E E E B E',
    mds: [`**Bruce Palmer**, presumably, and **Steven Stills**, notably - on 
    *Suite: Judy Blue Eyes*. **Mark Hanson** confirms that the tuning Stills used for this 
    song was E E E E B E.`,
    `Crazy as this tuning might appear at first glance, only three strings are re-tuned; 
    strings 6, 2 and 1 stay as is. String 5 must be tuned *down* to E, though string 4 would 
    be tuned *up* to E. String 3 would surely be tuned *down* to E.`,
    `"For tunings on the electrics and acoustics he [**Steven Stills**] uses standard,
    open E, open D, and a "**Bruce Palmer** Modal tuning", with the strings E, E, E, E, B,
    and E... This tuning was used for his interludes on *Suite: Judy Blue Eyes*." - 
    **Lowell Cauffiel**, article *Steven Stills* in Guitar Player, January 1976.`,
    `Used also by **Scott Ainslie**, for *Wayfaring Stranger* and other songs. 
    "The derivation of the tuning was **Stephen Stills's** *Suite: Judy Blue Eyes*,
    and also *4 + 20*. **John Hurt** also used the tuning for *Pay Day*." - **Scott Ainslie**,
    interview in Acoustic Guitar magazine, June  2010.`]
  }, {
      notes: 'F C F Ab C F',
      photos: [
        {name: 'Albert_Collins.jpg', width: 255}
      ],
      mds: [`Used by **Albert Collins**, as his standard tuning, with a capo to change 
      keys - positioned mostly at frets 5 (Bb minor), 7 (C minor), and 9 (D minor).`,
      `Of course, with him using a capo and being tuned to Fm, a lot of his vibrato notes 
      were not bent.`,
      `He, notes: 'D just hold a note at the top of the chord on the first 
      string, and shake the hell out of it." - **Coco Montoya**, in interview in Guitar 
      Player, May 2007. **Collins** played with his right hand thumb and forefinger,
      popping and snapping the strings.`,
      `Don't even try this tuning,  except on a solid body electric, with light strings -- 
      since every string is tuned *up* (some quite a ways). **Collins** played a blonde 
      maple-neck 1966 Fender Telecaster, strung up as follows: .010, .013, .015, .026, .032,
      .038. `,
      `String 1 up a half-step to F, string 5 up a minor third to C, string 4 up a minor 
      third to F, string 3 up a half-step to Ab, string 2 up a half-step to C, string 1 up 
      a half-step to F.`,
      `**Collins** famously played with a 100-150 long guitar cord, so that he could stroll 
      out into the audience, or down the street to a payphone to order pizza (just once,
      apparently).`,
      `Some of this information is from the **Fender Players Club**. `
      ]
    }, {
      notes: 'F A D G B E',
      mds: [`WA, March 2003. `,
      `This is just standard tuning, with the low E string screwed up a half step to F. 
      I stumbled upon this little trick while working up chords for **Rhett Miller's** 
       *There Is a World Inside the World*, in F. `,
      `I like using x87565 for the F chord (against colleague Jim R's 133211). 
      I found it all too easy, though, to hit the open low E string, which sounds 
      about as wrong as can be against an F major triad. Tweaking that open 6th string 
      up to F solved that problem and then some -- it gives me a low 1 note for the F 
      chord. Because I'm playing all the other chords for this song as 4 and 5 string 
      grabs, no other chords were injured in the process. `,
      `Next thing I knew, we were playing this song in G(!).`
      ]
    }, {
      notes: 'D G D G B D',
      photos: [
        {name: 'Charley_Patton.jpg', width: 200},
        {name: 'Keith_Richards.jpg', width: 100},
        {name: 'Bonnie_Raitt.jpg', width: 160}
      ],
      mds: [` Used by **Nick Drake** on *Rider on the Wheel*.  
      **Pat Kirtley** says that this tuning is derived from 5-string banjo tuning,
      and has been in use since at least 1880. This seems very likely, since the standard 
      tuning for 5-string banjo is g D G B D (low to high, with a light hign-tuned G string 
      for what would otherwise be the lowest string).  
      **Mary McCaslin** says it is "...probably the most popular open tuning."  
      Tune the 6th, 5th and first strings down a full tone. This tuning can be thought of as 
      a step beyond Double Drop-D tuning: the 5th string is also lowered a full step,
      leaving the 'middle' three strings as in standard tuning, in which they just happen 
      to form a G major triad.   
      Open G tuning has been used extensively for bottleneck slide-guitar styles by many of 
      the great Mississippi Delta bluesmen, notably **Charley Patton** - *High Water Everywhere*,
      *Screamin' And Hollerin' The Blues*, *Sun House*, **Robert Johnson** - 
      *Travelin' Riverside*, **Bukka White**, **Willie Brown**, **Muddy Waters** and 
      **Mance Lipscomb**.  
      It's been used more recently by blues-saturated players such as **John Hammond** - 
      *Drop Down Mama*, and **Bonnie Raitt** - *Write Me a Few of Your Lines*.   
      **Rory Block** claims, "Open G is incredibly versatile for practically every song 
      by **Robert Johnson**, **Mississippi John Hurt**, **Willie Brown**, **Charley Patton**,
      **Buddy Boy Hawkins**." - Acoustic Guitar magazine, Jan 2004.   
      **Phil and Don Everly** learned this tuning from their Aunt Hattie, and independently 
      from their dad Ike. Dark-haired **Don Everly** used it for his composition 
      *Give Me a Future*, and later for his power-chord part on *Wake Up Little Susie* 
      (capo-ed up 7 to D), with younger brother Phil playing in standard tuning, in D. 
      (This song, by the way, was written not by The Everlys, but by 
      **Felice and Boudleaux Bryant**.) - **David Simons**, article *Rock Foundation* 
      in Acoustic Guitar magazine, August 2000.    
      **Keith Richards** cites Don Everly's use of Open G as the direct inspiration for 
      his five-string variation. - minus the 6th string, presumably to keep the tonic G 
      in the bottom. **Richards** uses his Open G tuning  - with and without a slide - 
      as often as he uses standard tuning. - **David Simons**, article *Rock Foundation* 
      in Acoustic Guitar magazine, August 2000, and other sources, including **Mark Hanson**.`,
      
      `'Toward the end of the 60s, **Richards** had gradually made the switch from 
      open-E 'tuning (which he employed on rockers like *Street Fighting Man* 
      and *Jumpin' Jack Flash* to the now familiar open-G (his five-string tuning),
      thanks in large part to the influence of **[Gram] Parsons** and guitarist supreme 
      **Ry Cooder**. For *Wild Horses*, Richards wound up using both tunings.' 
      [12-string acoustic in open-G and open-E tuned Gibson six-string capo-ed up 
      three to G] - **David Simons**, article *The Rolling Stones step out of the limelight to 
      record "Wild Horses"* in Acoustic Guitar magazine,
      Dec 2000.              .`,
      `**Ike Turner** in turn, picked it up from **Keith Richards**, right down to the 
      eschewed string 6. "I used a G tuning on *Nutbush City Limits* that I learned 
      from **Keith Richards** when Tina and opened for the Rolling Stones in 1969. 
      I have a 5 string Tele clone tuned to G."  - **Dave Rubin**, article 
      *Rollin' on The Rhythm* in Guitar Player magazine, Dec 2006.            `,
        
      `Open G was one of **Leo Kottke's** favorite tunings, when he was first becoming 
      well-known, and it has also been used by **Joni Mitchell**, **Nathan La Franeer**,
      **Mary McCaslin**, **Jorma Kaukonen** - *Water Song*, **John Renbourn** - 
      *Old Mac Bladgitt*, **James Taylor** - *Love Has Brought Me Around*
      , **Ry Cooder** - *Available Space*, **Nancy Griffith** - *Love at the Five and Dime*, **Pat Kirtley**,
      and many other artists. It's **Eric Clapton's** favorite slide tuning. ' - 
      **Keith Wyatt**, article *Touch of Gold* in Guitar World Acoustic,
      Spring 2000.    **Mark Knopfler** reports, "When I started playing bottleneck at age 16,
      I began doing **Elmore James** stuff in [open] E. Later, when I became a fingerpicker,
      I was drawn to open G. I've never played much bottleneck on record. I really don't know 
      why I haven't, because I love it. [Knopfler's "bottleneck" is a brass slide turned 
      on a lathe by his dad.]  ' - **Andy Ellis**, article *Postcards from Shangri-La* in 
      Guitar Player, Jan 2005.`,
        
      `See also (below) the other 'Spanish Tuning' - Open A (E A E A C# E) which is the same 
      pattern up a whole step. It was also used by the Delta bluesman, **Robert Johnson**,
      at least.  
      Open G is also one of the most widely-used of the Hawaiian 'slack key' tunings. 
      In this tradition, it is called Taro Patch ' tuning.  
      "When you switch to open-G, the VG [auto-retuning Fender Stratocaster] naturally 
      tunes all strings to their even-tempered pitches. But **Sonny Landreth**,
      **Keith Richards** and other open-G masters often lower the second string slightly 
      so the major third [the 3] is in tune with the overtone series. 
      This adjustment dials out the dissonance, and makes those big one finger major chords 
      come alive." - **Jude Gold**, review of Fender VG Stratocaster in Guitar Player,
      June 2007.`,
         
      `The VG Stratocaster guitar noted above, by the way, doesn't actually retune the strings 
      at all; it digitizes the signals from the Roland hexaphonic (six-element) pickup,
      digitally shifts the pitches (to Drop-D, open-G, DADGAD or baritone (B E A D # B) tuning,
      and then mixes and converts these six data streams back to a single analog output.  
      I'm noticing, Apr 2001, that this tuning works very well for the key of D as well as for G.
      Either key faces the limitation of not having an open bass string for one of the three 
      principle triads. In G, there's no c bass for the IV chord, though one solution is to 
      use the open g on string 5, since it's the 5 of the C chord. In D, there's no a bass 
      for the V chord of D, and its 5 (e) is not handy either, but even with a slide on board,
      it may well be possible to fret some kind of an A or A7 chord. This A7sus4 works 
      for me: x2x020.  
      The suspended 4 tone adds some mystery, but since it is d, it can't be too far out of 
      line for the key of D. The other advantage for D is that it owns the deepest bass note,
      on open string 6.`
    ]}, {
    notes: 'g G D G B D',
    mds: [`This tuning especially reveals how the various
    open G tunings are (per **Pat Kirtley**) derived from the 
    standard tuning for 5-string banjo: g D G B D (low to high).   
    Replace the 6th string with a guitar 2nd string,
    and tune it to G, in unison with the G on the 3rd string. 
    You could use a guitar 3rd string, of course, but the unwound 
    2nd string rings more like the high-tuned G string 
    on a 5-string banjo.  
    This tuning was suggested by banjo 
    and guitar player **Alden Witham**, who uses it primarily 
    for the key of G.  
    He says, "I could see any banjo player 
    thinking of it, but nobody taught it to me. I had a beater 
    guitar and I had just started playing banjo 
    and liked the tuning. It ended up not sounding like banjo 
    at all; it sounds kind of Celtic, I guess. I play banjo 
    chords on the bottom four strings and use the top two for drones."`
  ]}, {
    notes: 'G G D G G A',
    mds: [`A favorite of **Sonic Youth**, used for *Dripping Dream*, *Unmade Bed*, *others*.`
  ]}, {
    notes: 'G G D G B D',
    photos: [{ name: 'Joni_Mitchell-02.jpg', width: 200}],
    mds: [`Used by **Joni Mitchell** on *This Flight Tonight* 
    (from *Blue*, 1971).  
    Acoustic Guitar magazine notes that **Joni** 
    tunes the 6th string, like the 5th string, down to G.  
    That's a long ways down from E - a major 6th - but after 
    listening to the recording, and trying it myself,
    I believe it. She must have used a heavier string than my 
    light .052, which plays and sounds like a clothesline when 
    I tune it down that far. `
  ]}, {
    notes: 'G G C G C D',
    mds: [
      `Used by **Ani DiFranco**.  
      Heavier 6th string down to G, presumably. `
  ]}, {
    notes: 'G B D G B D',
    mds: ['6th string tuned *up* to G, presumably?!'
  ]}, {
    notes: 'G B D G A D',
    mds: ['Hammer-on, or slide (dobro-style) the A of the 2nd string up to B -- the 3 of the G major chord.'
  ]}, {
    notes: 'G Bb D G Bb D',
    mds: [
      `It's unnerving to tune the 6th string up that far - 
      lots of tension.  
      A heavy string could be tuned *down* that far.`
  ]}, {
    notes: 'D G D A Bb E',
    mds: [`Used by **David Russell** for **Ben Verdery**'s composition 
    *Now and Ever*. For one of the movements, a capo is clamped at 
    fret 9 (except for on string 6) which transforms the tuning 
    to D E B F# G C#.`
  ]}, {
    notes: 'D G D G Bb D',
    mds: [`Used by **Mary McCaslin**. Tune the 6th, 5th, 2nd and 
    1st strings *down*, 4th and 3rd stay put.`
  ]}, {
    notes: 'D G D G Bb C',
    mds: [`Used by **Michael Hedges**, for *Two Days Old*. 
    Strings 6 and 5 *down* a whole-step, string 2 *down* 
    a minor second (half-step), string 1 *down* a major third.`
  ]}, {
    notes: 'D G D G D G',
    mds: [`Used by **Nick Drake** on *Man in a Shed*.`],
    photos: [
      {name: 'Nick_Drake_01.jpg', width: 100}
  ]}, {
    notes: 'D G D G D F#',
    mds: ['Used by **Nick Drake** on *Parasite*.'
  ]}, {
    notes: 'D G D D A D',
    mds: ['Used by **Nick Drake** on *Road*.'
  ]}, {
    notes: 'D G D G C D',
    photos: [{name: 'Martin_Simpson.jpg', width: 200 }],
    mds: [`Used by **Martin Simpson** for *Seven Yellow Gypsies*(?). **Ralph Denyer** says 
    this one is sometimes called *Sawmill tuning* (a term more often applied to a banjo tuning).  
    **Martin Simpson** notes that "...in England they have Gsus4 [DGDGCD] and Gsus4/4 [CGDGCD] 
    which are straight lifts from the five-string banjo." - Acoustic Guitar magazine, March 2004.`
  ]}, {
    notes: 'D G D G G D',
    mds: [`Used by **John Fahey**, for *I Am the Resurrection* 
    (from Fahey's 1965 album *The Transfiguration of Blind Joe Death*),
    and by **Michael Hedges** for *Come Together*.  
    Strings 6 and 5 *down* a whole-step, string 2 *down* a major third,
    string 1 *down* a whole-step.`
  ]}, {
    notes: 'D G D G B E',
    preferredNote12: 7,
    photos: [{name: 'David_Gilmour.jpg', width: 100}],
    mds: [` **Randy Scruggs** and **Chet Atkins** - *Both Sides Now* and *Yellow Bird*,
      **John Renbourn** - *John's Tune*, **David Gilmour**, **Pat Kirtley** ...  
      This is just a step beyond Drop-D tuning: the 5th string is also lowered a full step,
      leaving the upper four strings as in standard tuning.  
      Used by **David Gilmour** ... "I use that for *The Great Gig In the Sky* [on a lap steel 
      guitar]. That allows me to form a minor chord on the first three strings, and a major 
      chord with all the other strings." - **David Gilmour**, 1997.  
      [Good idea: those chords are the relative minor and relative major of each other: Em and G,
      respectively, in open position.]  
      "Open tunings are by definition rather restrictive, so I found a tuning which is kind 
      of an open G6. The first four strings are the same as a regular guitar -- E B G D -- and 
      if you tune the bottom A down to G and the E down to D, you get a five-string open G 
      chord; but you've also got a three-string E minor chord at the top, so you can do quite 
      effective majors and minors. And that's the tuning I tend to use quite a bit, and that's 
      the one I originally laid down for *The Great Gig in the Sky*." - 
      interview with **David Gilmour**, titled *Shine On*, Guitar World magazine, March 2006`
    ]}, {
      notes: 'D G D G A D',
      mds: [`Used by **Nick Drake**.  Strings 6, 5, 2 and 1 dropped a whole step,
      with strings 4 and 3 left as is. The A on string 2 is begging to be hammered into B,
      the natural 3, to complete the G major triad.  `
    ]}, {
      notes: 'D G D F# B D',
      mds: [`Hawaiian slack key tuning used by **Ledward Kaapana**, and many other Hawaiian 
      players, probably. Also used by **Mark Hanson** for his arrangement 
      of *Angels We Have Heard On High*. I don't think that Hanson uses the # for a Gmaj7 
      (in this song at least). It comes in very handy, though, for the D6 chords that he plays 
      in it.  `
    ]}, {
      notes: 'D G C G C D',
      mds: [`Used by **Jimmy Page**, for *The Rain Song*. Strings 6, 5 and 4 dropped a 
      whole step, string 2 raised a half-step, string 1 dropped a whole step.  '- Duly 
      noted by **Joe Ongie**, 13 June 2007.  `
    ]}, /** DOUBLE {
      notes: 'C G D G C D',
      mds: [`Long used by many players, including **Martin Simpson**, **Seth Austin**,
       **Tony McManus**, **Chris Proctor**, **Anton Emery**, **Joni Mitchell**, and **Sonic Youth**. 
      Named by **Steve Baughman**, after the Orkney Islands off the northern coast of Scotland.`,
      `String 6 is dropped way back to C (after replacement with a heavier gauge string). 
      String 5 is lowered a whole step to G, strings 4 and 3 are left as is, string 2 is raised a 
      half step to C, and string 1 is lowered a whole step to D.`,
      ` **Martin Simpson** notes that "...in England they have Gsus4 [DGDGCD] and Gsus4/4 [CGDGCD] 
      which are straight lifts from the five-string banjo." - Acoustic Guitar magazine, March 2004. `,
      `While many alternate tunings are more or less married to a particular key, Orkney works well 
      for at least the keys of G and C, and **Steve Baughman** also uses it for the key of D, and 
      it may be usable for Cm as well. This flexibility owes much to open bass strings set to C,
      G and D respectively (rather than to a 1, a 5, and an octave of the 1, as in many other 
        tunings).`,
      `DADGAD players note: many chords and other fingerings are directly transportable to Orkney 
      tuning, because the intervals between Orkney's strings 1-5 are the same as between DADGAD's 
      strings 2-6.`,
      `"It is a wonderful tool for melodic (non-linear) playing, in which you avoid playing subsequent 
      notes on the same string. This technique (**Pierre Bensusan** calls it "harp style") allows 
      for a very smooth and gentle delivery of the melody and avoids the staccato effect that marks 
      many guitar arrangements of fiddle tunes." (**Steve Baughman**)`,
      `In an e-mail message, **Baughman** further disclosed that, "It is also becoming a tuning 
      used by singer-songwriters as accompaniment." - article *Orkney Tuning* by **Steve Baughman**,
      Acoustic Guitar magazine, Dec 2008`
      ]
    },  */ {
      notes: 'C G D G B D',
      mds: [ `**Michael Hedges**, for *Gospel* and *Eleven Small Roaches*.`,
      `It seems likely that this tuning would be for the key of G, with a big low C for the IV chord. 
      Strings 6, 3, and 1 each lowered to their target pitches, almost surely.`
      ]
    }, {
      notes: 'C G D G A D',
      mds: [`This tuning can be thought of as DADGAD with the 5th and 6th strings dropped yet 
      another whole step.`, `Used by **Lawrence Juber**, who sometimes switches to it from DADGAD.`,
      `"It works well for the keys of C, C minor, G, and G minor."  **Lawrence Juber**, Fingerstyle 
      Guitar magazine, Oct 2003.`
      ]
    }, {
      notes: 'C G G D G D',
      mds: [`Used by **Nels Cline**, for *Grow Closer* and *Red Line to Greenland*. 
      Tune each string *down* to target pitches, very likely. In key of C, or G with the low C string supporting the IV chord.`
      ]
    }
  ];
  constructor() {}
}
